import { useMutation } from '@apollo/client';
import React from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const datasetSourceArtifactsFragment = graphql(`
  fragment DatasetSourceArtifacts on EvalDatasets @_unmask {
    id
    datasetSourceArtifacts {
      id
    }
  }
`);

const deleteDatasetSourceArtifactMutation = graphql(`
  mutation deleteDatasetSourceArtifact($id: uuid!) {
    deleteEvalDatasetSourceArtifactsByPk(id: $id) {
      __typename
      id
    }
  }
`);

export interface RemoveArtifactFromDatasetActionProps {
  datasetSourceArtifactId: string;
  datasetId: string;
}

export const RemoveArtifactFromDatasetAction: React.FC<
  RemoveArtifactFromDatasetActionProps
> = ({ datasetSourceArtifactId, datasetId }) => {
  const toast = useToast();
  const [deleteDatasetSourceArtifact] = useMutation(
    deleteDatasetSourceArtifactMutation,
    {
      onError: () => {
        toast.error('Failed to remove artifact from dataset');
      },
      optimisticResponse: {
        deleteEvalDatasetSourceArtifactsByPk: {
          __typename: 'EvalDatasetSourceArtifacts' as const,
          id: datasetSourceArtifactId,
        },
      },
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: datasetSourceArtifactsFragment,
            key: {
              id: datasetId,
            },
          },
          (existing) => {
            return data?.deleteEvalDatasetSourceArtifactsByPk
              ? {
                  id: datasetId,
                  datasetSourceArtifacts: (
                    existing?.datasetSourceArtifacts ?? []
                  ).filter(
                    (dsa) =>
                      dsa.id !== data.deleteEvalDatasetSourceArtifactsByPk?.id,
                  ),
                }
              : existing;
          },
        );
      },
    },
  );

  const removeArtifactFromDataset = async () => {
    await deleteDatasetSourceArtifact({
      variables: {
        id: datasetSourceArtifactId,
      },
    });
  };

  return (
    <AlertDialog>
      <SimpleTooltip label="Remove this artifact from the dataset">
        <AlertDialogTrigger asChild>
          <Button variant="outline">
            <Icon name="Trash2" size="xl" className="text-bg-orange-strong" />
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will remove the artifact from the dataset.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={removeArtifactFromDataset}
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
